import React, { useState } from 'react';
import { AiOutlineHome, AiOutlineUser, AiOutlineAppstore } from 'react-icons/ai';
import Users from './Users';
import Pins from './Pins';
import Coins from './Coins';
import RedemptionRequests from './RedemptionRequests';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import AdminApproval from './AdminApproval';

export default function AdminPanel() {
  const [tab, setTab] = useState('users');
  const navigate = useNavigate();

  const tabList = [
    {
      key: 'users',
      label: 'Users',
      icon: <AiOutlineUser className="mr-2 text-lg" />,
    },
    {
      key: 'posts',
      label: 'Posts',
      icon: <AiOutlineAppstore className="mr-2 text-lg" />,
    },
    {
      key: 'approval',
      label: 'Approval',
      icon: <AiOutlineUser className="mr-2 text-lg" />,
    },
    {
      key: 'coins',
      label: 'Coins',
      icon: (
        <svg className="mr-2 text-lg" width="1em" height="1em" viewBox="0 0 20 20" fill="currentColor">
          <circle cx="10" cy="10" r="8" className="text-yellow-400" />
          <text x="10" y="14" textAnchor="middle" fontSize="10" fill="#fff" fontWeight="bold">$</text>
        </svg>
      ),
    },
    {
      key: 'redeem',
      label: 'Redemption Requests',
      icon: (
        <svg className="mr-2 text-lg" width="1em" height="1em" viewBox="0 0 20 20" fill="currentColor">
          <rect x="3" y="6" width="14" height="8" rx="2" className="text-green-400" />
          <text x="10" y="13" textAnchor="middle" fontSize="8" fill="#fff" fontWeight="bold">R</text>
        </svg>
      ),
    },
  ];

  const buttonClass = (currentTab) =>
    `flex items-center px-7 py-2 rounded-full font-semibold text-base shadow-lg transition-all duration-200
    ${tab === currentTab
      ? 'bg-gradient-to-r from-blue-500 via-teal-400 to-purple-500 text-white scale-105 ring-2 ring-blue-300'
      : 'bg-white/60 dark:bg-gray-700/60 text-gray-700 dark:text-gray-200 hover:bg-blue-100 dark:hover:bg-gray-600'
    }`;

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-start overflow-x-hidden">
      {/* Animated Gradient Background */}
      <div className="absolute inset-0 -z-10 animate-gradient bg-[linear-gradient(120deg,#a1c4fd,#c2e9fb,#fbc2eb,#a6c1ee,#fbc2eb,#a1c4fd)] bg-[length:400%_400%] dark:bg-[linear-gradient(120deg,#232946,#1a2233,#232946,#232946,#1a2233,#232946)]" />

      {/* Glassmorphism Card */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-tr from-blue-200/60 via-teal-100/60 to-purple-200/60 dark:from-gray-900/80 dark:via-slate-900/80 dark:to-blue-900/80 backdrop-blur-2xl"
      />

      <button
        onClick={() => navigate('/')}
        className="absolute top-6 left-6 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors bg-white/70 dark:bg-gray-800/70 p-2 rounded-full shadow-md"
        title="Back to Home"
      >
        <AiOutlineHome className="text-2xl" />
      </button>

      <h1 className="text-5xl font-extrabold mb-10 mt-12 text-center bg-gradient-to-r from-blue-600 via-teal-500 to-purple-500 bg-clip-text text-transparent drop-shadow-lg tracking-tight">
        Admin Dashboard
      </h1>

      {/* Tabs */}
      <div className="grid grid-cols-2 sm:flex sm:flex-wrap justify-center gap-2 sm:gap-3 md:gap-6 mb-6 md:mb-12 w-full px-2">
        {tabList.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={
              buttonClass(t.key) +
              " w-full sm:w-auto justify-center px-3 sm:px-6 py-2 text-sm sm:text-base"
            }
          >
            {t.icon}
            <span className="truncate">{t.label}</span>
          </button>
        ))}
      </div>

      {/* Animated Content Card with Gradient Border */}
      <div className="w-full max-w-5xl px-4">
        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, scale: 0.96, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: -30 }}
            transition={{ duration: 0.35, ease: 'easeInOut' }}
            className="relative rounded-3xl p-0 shadow-2xl"
          >
            {/* Gradient Border */}
            <div className="absolute inset-0 rounded-3xl pointer-events-none z-0 border-0.5 border-transparent bg-gradient-to-tr from-blue-400 via-teal-300 to-purple-400 opacity-80 blur-[2px]" />
            {/* Glassmorphism Card */}
            <div className="relative z-10 bg-white/80 dark:bg-gray-800/80 rounded-3xl p-10 backdrop-blur-xl border border-white/30 dark:border-gray-700/40">
              {tab === 'users' && <Users />}
              {tab === 'posts' && <Pins />}
              {tab === 'approval' && <AdminApproval />}
              {tab === 'coins' && <Coins />}
              {tab === 'redeem' && <RedemptionRequests />}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Custom Gradient Animations */}
      <style>
        {`
          .bg-gradient-radial {
            background: radial-gradient(circle at 60% 40%, #a1c4fd 0%, #c2e9fb 40%, #fbc2eb 70%, #a6c1ee 100%);
          }
          @keyframes bgPulse {
            0%,100% { filter: blur(0px) brightness(1); }
            50% { filter: blur(8px) brightness(1.08); }
          }
          .animate-bgPulse {
            animation: bgPulse 12s ease-in-out infinite;
          }
          @keyframes gradient {
            0%,100% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
          }
          .animate-gradient {
            animation: gradient 18s ease-in-out infinite;
          }
        `}
      </style>
    </div>
  );
}